
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EyeOff, Eye } from '@untitledui/icons';
import { useSupabaseAuth } from '@kordbooks/shared-auth';
import { getSupabaseClient } from '@kordbooks/supabase-client';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { UntitledLogo } from '@/components/foundations/logo/untitledui-logo';
import { cx } from '@/utils/cx';
import { validateEmail, validatePassword } from '@kordbooks/shared-auth';

type MFATotpFactor = {
  id: string;
  status?: string;
  friendly_name?: string;
};

type Step = 'credentials' | 'mfa' | 'email-otp';

export default function SignIn() {
  const navigate = useNavigate();
  const {
    signIn,
    loading: authLoading,
    error: authError,
    listFactors,
    challengeAndVerifyMFA,
  } = useSupabaseAuth();

  const supabase = getSupabaseClient();

  const [step, setStep] = useState<Step>('credentials');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formError, setFormError] = useState('');

  // MFA state
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  // Email OTP fallback state
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpError, setEmailOtpError] = useState('');

  const isSubmitting = isLoading || authLoading;

  const moveToDashboard = () => {
    navigate('/portal/dashboard');
  };

  const startMfaIfNeeded = async () => {
    // Check the authenticator assurance level to see if MFA is required
    const { data: aalData, error: aalError } =
      await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

    if (aalError) {
      throw aalError;
    }

    const currentLevel = aalData?.currentLevel;
    const nextLevel = aalData?.nextLevel;

    // If nextLevel is aal2 and currentLevel is not, user has an MFA factor that must be verified
    if (nextLevel === 'aal2' && currentLevel !== 'aal2') {
      const factors = (await listFactors()) as MFATotpFactor[];

      if (!Array.isArray(factors) || factors.length === 0) {
        // No factors found; fall back to normal flow
        moveToDashboard();
        return;
      }

      // For now choose the first TOTP factor (you can extend this later if you add more)
      const verifiedFactor =
        factors.find((f) => f.status === 'verified') ?? factors[0];

      if (!verifiedFactor?.id) {
        moveToDashboard();
        return;
      }

      setMfaFactorId(verifiedFactor.id);
      setMfaCode('');
      setMfaError('');
      setStep('mfa');
    } else {
      // No additional factor required
      moveToDashboard();
    }
  };

  const handleCredentialsSubmit = async () => {
    setEmailError('');
    setPasswordError('');
    setFormError('');
    setMfaError('');
    setEmailOtpError('');

    let isValid = true;

    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    // Validate password
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (!validatePassword(password)) {
      setPasswordError('Password must be at least 8 characters');
      isValid = false;
    }

    if (!isValid) return;

    try {
      setIsLoading(true);

      // First factor: email + password
      await signIn({ email, password });

      // After sign-in, decide if we need to do MFA
      await startMfaIfNeeded();
    } catch (err) {
      const apiError = err as { message?: string };
      setFormError(apiError.message || 'Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async () => {
    setFormError('');
    setMfaError('');

    const code = mfaCode.trim();

    if (!code) {
      setMfaError('Authentication code is required');
      return;
    }

    if (!/^[0-9]{6}$/.test(code)) {
      setMfaError('Code must be a 6-digit number');
      return;
    }

    if (!mfaFactorId) {
      setFormError('Your MFA session expired. Please sign in again.');
      setStep('credentials');
      return;
    }

    try {
      setIsLoading(true);

      // Use the shared helper that does challenge + verify in one step
      await challengeAndVerifyMFA(mfaFactorId, code);

      // On success, Supabase refreshes the session to aal2 in the background,
      // and we can now treat the user as fully authenticated.
      moveToDashboard();
    } catch (err) {
      const apiError = err as { message?: string };
      setMfaError(apiError.message || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailOtpRequest = async () => {
    setFormError('');
    setMfaError('');
    setEmailOtpError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);

      // Drop the partial password+MFA session so the OTP login is clean.
      await supabase.auth.signOut();

      const { error } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: {
          // Prevent accidental sign-up if email doesn’t exist.
          shouldCreateUser: false,
          emailRedirectTo: window.location.origin + '/sign-in',
        },
      });

      if (error) {
        throw error;
      }

      setStep('email-otp');
      setEmailOtpCode('');
      setEmailOtpError('');
    } catch (err: any) {
      setFormError(
        err.message || 'Failed to send email code. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailOtpSubmit = async () => {
    setFormError('');
    setEmailOtpError('');

    const code = emailOtpCode.trim();
    if (!code) {
      setEmailOtpError('Verification code is required');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setEmailOtpError('Code must be a 6-digit number');
      return;
    }

    try {
      setIsLoading(true);

      const { error } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: code,
        type: 'email',
      });

      if (error) {
        throw error;
      }

      // At this point Supabase has created a valid session (AAL1).
      moveToDashboard();
    } catch (err: any) {
      setEmailOtpError(err.message || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 'mfa') {
      await handleMfaSubmit();
    } else if (step === 'email-otp') {
      await handleEmailOtpSubmit();
    } else {
      await handleCredentialsSubmit();
    }
  };

  const handleResetToCredentials = () => {
    setStep('credentials');
    setPassword('');
    setMfaCode('');
    setMfaError('');
    setMfaFactorId(null);
    setEmailOtpCode('');
    setEmailOtpError('');
    setFormError('');
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-primary px-4 py-8">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <UntitledLogo className="h-8" />
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-display-sm font-semibold text-primary md:text-display-md">
            {step === 'mfa'
              ? 'Enter authentication code'
              : step === 'email-otp'
              ? 'Check your email'
              : 'Login to your account'}
          </h1>
          {step === 'mfa' && (
            <p className="mt-2 text-sm text-tertiary">
              Two-factor authentication is enabled for this account. Enter the
              code from your authenticator app to finish signing in.
            </p>
          )}
          {step === 'email-otp' && (
            <p className="mt-2 text-sm text-tertiary">
              Enter the 6-digit code that was sent to your email address to
              finish signing in.
            </p>
          )}
        </div>

        {/* Top-level error (from sign-in / general issues) */}
        {(formError || authError) && (
          <div className="mb-6 rounded-lg bg-error-primary/10 p-4 text-sm text-error-primary">
            {formError || authError?.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {/* Email – always visible so user knows which account */}
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-primary"
            >
              Email address
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(value) => {
                setEmail(value);
                setEmailError('');
              }}
              isInvalid={!!emailError}
              size="md"
              isDisabled={isSubmitting || step === 'mfa' || step === 'email-otp'}
            />
            {emailError && (
              <p className="text-sm text-error-primary">{emailError}</p>
            )}
          </div>

          {step === 'credentials' ? (
            <>
              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-primary"
                  >
                    Password
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-sm text-brand-secondary hover:text-brand-secondary_hover"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(value) => {
                      setPassword(value);
                      setPasswordError('');
                    }}
                    isInvalid={!!passwordError}
                    size="md"
                    isDisabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-quaternary hover:text-fg-quaternary_hover"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="size-5" />
                    ) : (
                      <Eye className="size-5" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-sm text-error-primary">{passwordError}</p>
                )}
              </div>

              {/* Remember me – visual only, no auth effect yet */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  className="cursor-pointer rounded border-secondary focus:ring-2 focus:ring-brand"
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="remember"
                  className="cursor-pointer text-sm text-tertiary"
                >
                  Remember me for 30 days
                </label>
              </div>
            </>
          ) : step === 'mfa' ? (
            <>
              {/* MFA Code */}
              <div className="space-y-2">
                <label
                  htmlFor="mfa-code"
                  className="block text-sm font-medium text-primary"
                >
                  Authentication code
                </label>
                <p className="text-xs text-tertiary">
                  Enter the 6-digit code from your authenticator app.
                </p>
                <Input
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(value) => {
                    setMfaCode(value);
                    setMfaError('');
                  }}
                  maxLength={6}
                  isInvalid={!!mfaError}
                  size="md"
                  isDisabled={isSubmitting}
                  className="font-mono tracking-widest text-center text-lg"
                />
                {mfaError && (
                  <p className="text-sm text-error-primary">{mfaError}</p>
                )}
              </div>

              {/* Email fallback trigger */}
              <button
                type="button"
                onClick={handleEmailOtpRequest}
                className="text-xs text-tertiary underline hover:text-primary"
                disabled={isSubmitting}
              >
                Don't have access to your authentication app? Get code via email
              </button>

              <button
                type="button"
                onClick={handleResetToCredentials}
                className="text-xs text-tertiary underline hover:text-primary"
                disabled={isSubmitting}
              >
                Back to password login
              </button>
            </>
          ) : (
            <>
              {/* Email OTP Code */}
              <div className="space-y-2">
                <label
                  htmlFor="email-otp"
                  className="block text-sm font-medium text-primary"
                >
                  Email verification code
                </label>
                <p className="text-xs text-tertiary">
                  Enter the 6-digit code sent to your email address.
                </p>
                <Input
                  id="email-otp"
                  name="email-otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="000000"
                  value={emailOtpCode}
                  onChange={(value) => {
                    setEmailOtpCode(value);
                    setEmailOtpError('');
                  }}
                  maxLength={6}
                  isInvalid={!!emailOtpError}
                  size="md"
                  isDisabled={isSubmitting}
                  className="font-mono tracking-widest text-center text-lg"
                />
                {emailOtpError && (
                  <p className="text-sm text-error-primary">{emailOtpError}</p>
                )}
              </div>

              <button
                type="button"
                onClick={handleResetToCredentials}
                className="text-xs text-tertiary underline hover:text-primary"
                disabled={isSubmitting}
              >
                Back to password login
              </button>
            </>
          )}

          <Button
            type="submit"
            color="primary"
            size="md"
            isDisabled={isSubmitting}
            className={cx('w-full', isSubmitting && 'opacity-75')}
          >
            {isSubmitting
              ? step === 'mfa'
                ? 'Verifying...'
                : step === 'email-otp'
                ? 'Verifying...'
                : 'Signing in...'
              : step === 'mfa' || step === 'email-otp'
              ? 'Verify code'
              : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-tertiary">
          Don't have an account?{' '}
          <a
            href="/sign-up"
            className="font-medium text-brand-secondary hover:text-brand-secondary_hover"
          >
            Sign up
          </a>
        </p>
      </div>
    </div>
  );
}
